import { Avatar, Box, Checkbox, FormControlLabel, TextField, Typography, Link, Grid, CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { setShowLogin, validateUser, setUserInfo, setRedirectUrl, setInValidUser } from "../Redux/user/slice";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { ForgotPasswordModal } from "./ForgotPasswordModal";

export function LoginModal() {
  const dispatch = useDispatch();
  const { showLogin, callBack, invalidUser, redirectUrl, isUserLoggedIn, user, account, isFetching } = useSelector((state) => state.user);
  const userId = user?.userId;
  const accountId = account?.accountId;
  const [passwordHelpText, setPasswordHelpText] = React.useState("");
  const [userHelpText, setUserHelpText] = React.useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const redirectUrlRef = React.useRef(null);
  
  // Map invalidUser messages to appropriate form field messages
  useEffect(() => {
    if (invalidUser) {
      // Always show error on password field for any login failure
      setPasswordHelpText(<FormattedMessage id="login.invalid.credentials" defaultMessage="Invalid email or password" />);
    } else {
      // Clear error when invalidUser is cleared
      setPasswordHelpText("");
      setUserHelpText("");
    }
  }, [invalidUser]);

  useEffect(() => {
    const redirectUrl = redirectUrlRef.current;
    if (redirectUrl && !invalidUser && isUserLoggedIn) {
      dispatch(
        setUserInfo({
          accountId: account?.accountId,
          userId: user?.userId,
        })  
      );
    }
  }, [invalidUser, isUserLoggedIn, dispatch, account, user, redirectUrlRef]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    
    // Clear previous error messages
    setUserHelpText("");
    setPasswordHelpText("");
    
    // Form validation with translated error messages
    if (!data.get("email")) {
      setUserHelpText(<FormattedMessage id="login.email.required" defaultMessage="Please provide an email address" />);
      return;
    }
    if (!data.get("password")) {
      setPasswordHelpText(<FormattedMessage id="login.password.required" defaultMessage="Please provide a password" />);
      return;
    }

    redirectUrlRef.current = redirectUrl;

    dispatch(
      validateUser({
        email: data.get("email"),
        password: data.get("password"),
        cb: callBack,
      })
    );
  };

  const handleClose = () => {
    dispatch(setShowLogin({ value: false, cb: null }));
    dispatch(setUserInfo(null));
    dispatch(setRedirectUrl({ redirectUrl: "" }));
    dispatch(setInValidUser(null));
    setPasswordHelpText("");
    setUserHelpText("");
  };

  const handleForgotPasswordClick = (e) => {
    e.preventDefault();
    setShowForgotPassword(true);
  };

  return (
    <Modal isOpen={showLogin} style={{ height: "auto" }} toggle={handleClose} backdrop onBackdropClick={handleClose} onClosed={() => { document.body.classList.remove('modal-open'); document.body.style.overflow = ''; document.body.style.paddingRight = ''; }}>    
      <ModalHeader style={{ justifyContent: "center", alignItems: "center" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255,255,255,0.8)",
            padding: 2,
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            <FormattedMessage id="loginmodal.title" />
          </Typography>
        </Box>
      </ModalHeader>
      <ModalBody>
        <Grid container>
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexFlow: "column nowrap",
              width: "100%",
              rowGap: "1em",
            }}
          >
            <Grid item>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label={<FormattedMessage id="userInput.email" />}
                name="email"
                autoComplete="email"
                onKeyDown={() => {
                  setUserHelpText("");
                  dispatch(setInValidUser(null));
                }}
                error={userHelpText !== ""}
                helperText={userHelpText}
                autoFocus
                sx={{ margin: "0" }}
                type="email"
              />
            </Grid>
            <Grid item>
              <TextField
                margin="normal"
                helperText={passwordHelpText}
                required
                fullWidth
                name="password"
                label={<FormattedMessage id="userInput.pwd" />}
                type="password"
                onKeyDown={() => {
                  setPasswordHelpText("");
                  dispatch(setInValidUser(null));
                }}
                id="password"
                error={passwordHelpText !== ""}
                autoComplete="current-password"
                sx={{ margin: "0" }}
              />
            </Grid>
            <Grid item>
              <FormControlLabel control={<Checkbox value="remember" color="primary" />} label={<FormattedMessage id="loginmodal.remember" />} />
            </Grid>
            {/* Only render this error box if there's an error that wasn't handled in the fields above */}
            {invalidUser && !passwordHelpText && !userHelpText && (
              <Typography
                color={"#f00"}
                sx={{
                  background: "#ff00001a",
                  width: "100%",
                  textAlign: "center",
                }}
              >
                {invalidUser}
              </Typography>
            )}
            <Grid item>
              <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
                {isFetching ? <CircularProgress size={24} /> : <FormattedMessage id="button.signin" />}
              </Button>
            </Grid>
            <Grid container>
              <Grid item xs>
                <Link href="#" variant="body2" onClick={handleForgotPasswordClick}>
                  <FormattedMessage id="loginmodal.forgot" />
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </ModalBody>

      <ModalFooter style={{ background: "#061534", color: "white" }}>
        <Grid sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <FormattedMessage id="loginmodal.register" />&nbsp;<a style={{ color: "red" }} href="mailto: info@vertocommodities.io">
            <FormattedMessage id="loginmodal.register.click" />
          </a>
        </Grid>
      </ModalFooter>

      <ForgotPasswordModal 
        isOpen={showForgotPassword} 
        onClose={() => setShowForgotPassword(false)} 
      />
    </Modal>
  );
}